<template>
  <span
    class="qc-basis-chip"
    :class="`qc-basis-${slug}`"
    :title="$t(`messages.evaluation.qualityCheck.basisHint.${slug}`)"
    data-testid="qc-finding-basis"
  >
    <!--
      FOUR classes, one grey hue, four tones: darker + solid = surer
      (design-spec §16.5, prototype v3's b4..b1). `märkimata` and `mõlemad` are
      the two low-trust classes: dashed outline plus a warning glyph. `mõlemad`
      ranks LOWEST (1/4), never highest — the one known fabricated blocking
      defect cited a standard and practice at once (§2.2). Asserted by CLASS in
      spec 36, because a class survives a theme change and a hex value does not.
    -->
    <span
      v-if="lowTrust"
      class="pi pi-exclamation-triangle text-[0.625rem] text-amber-500"
      aria-hidden="true"
    ></span>
    {{ $t(`messages.evaluation.qualityCheck.basis.${slug}`) }}
    <span class="tabular-nums">{{ rank }}/4</span>
  </span>
</template>

<script setup>
import { computed } from 'vue'

/**
 * RED-351 — the basis chip: which ground a finding stands on. Shared by the
 * finding card and the "Kuidas tähelepanekuid lugeda" legend.
 */
const props = defineProps({
  /** standard | praktika | märkimata | mõlemad */
  kind: { type: String, default: 'märkimata' },
})

const SLUG = { standard: 'standard', praktika: 'praktika', märkimata: 'markimata', mõlemad: 'molemad' }
const RANK = { standard: 4, praktika: 3, märkimata: 2, mõlemad: 1 }

const slug = computed(() => SLUG[props.kind] ?? 'markimata')
const rank = computed(() => RANK[props.kind] ?? 2)
const lowTrust = computed(() => rank.value <= 2)
</script>

<style scoped>
@reference '../../../../css/tailwind.css';

.qc-basis-chip {
  @apply inline-flex shrink-0 cursor-help items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold;
}

/* 4/4 — cites a standard the reader can look up. Darkest, solid. */
.qc-basis-standard {
  @apply border-gray-400 bg-gray-300 text-gray-900;
}

/* 3/4 — approver practice, no standard behind it. */
.qc-basis-praktika {
  @apply border-gray-300 bg-gray-200 text-gray-700;
}

/* 2/4 — the machine named no basis at all. */
.qc-basis-markimata {
  @apply border-dashed border-gray-300 bg-gray-100 text-gray-500;
}

/* 1/4 — cites a standard AND practice at once: lowest trust, on purpose. */
.qc-basis-molemad {
  @apply border-dashed border-gray-400 bg-gray-50 text-gray-600;
}
</style>
